<template>
  <div class="note-preview" ref="host">
    <SafeHtml :html="html" />
    <Lightbox :show="lightboxOpen" :images="images" :start-index="startIndex" @close="close" />
    <FilePreview
      :show="filePreviewOpen"
      :attachment="filePreviewAttachment"
      :zIndex="10080"
      @close="filePreviewOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import SafeHtml from "@/components/ui/SafeHtml.vue";
import Lightbox from "@/components/ui/Lightbox.vue";
import FilePreview from "@/components/ui/FilePreview.vue";
import type { PreviewAttachment } from "@/components/ui";

const props = defineProps<{ html: string | null | undefined }>();

const host = ref<HTMLElement | null>(null);
const lightboxOpen = ref(false);
const startIndex = ref(0);
const images = ref<string[]>([]);
const filePreviewOpen = ref(false);
const filePreviewAttachment = ref<PreviewAttachment | null>(null);
let clickHandler: ((e: Event) => void) | null = null;

const EXT_MIME: Record<string, string> = {
  pdf: "application/pdf",
  txt: "text/plain",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  zip: "application/zip",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  svg: "image/svg+xml",
  mp3: "audio/mpeg",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
};

function humanSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n >= 10 || i === 0 ? Math.round(n) : n.toFixed(1)} ${units[i]}`;
}

function variantFor(mime: string, name: string): string {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (mime === "application/pdf" || ext === "pdf") return "pdf";
  if (/^image\//.test(mime) || /^(png|jpe?g|gif|webp|svg|bmp)$/.test(ext)) return "img";
  if (/^video\//.test(mime) || /^(mp4|webm|mov|avi|mkv)$/.test(ext)) return "vid";
  if (/^audio\//.test(mime) || /^(mp3|wav|ogg|m4a|aac|flac)$/.test(ext)) return "aud";
  if (/zip|compressed/.test(mime) || /^(zip|7z|rar|tar|gz)$/.test(ext)) return "zip";
  if (/(word|document)/.test(mime) || /^(docx?|rtf)$/.test(ext)) return "doc";
  if (/(sheet|excel)/.test(mime) || /^(xlsx?|csv)$/.test(ext)) return "xls";
  return "file";
}

function iconSvg(variant: string): string {
  if (variant === "aud") {
    return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>`;
  }
  const base = `<path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/><polyline points="14 2 14 8 20 8"/>`;
  let inner = "";
  if (variant === "pdf" || variant === "doc") {
    inner = `<line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="9" y2="9"/>`;
  } else if (variant === "xls") {
    inner = `<line x1="8" y1="13" x2="10" y2="13"/><line x1="14" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="10" y2="17"/><line x1="14" y1="17" x2="16" y2="17"/>`;
  } else if (variant === "zip") {
    inner = `<line x1="10" y1="13" x2="10" y2="13.01"/><line x1="10" y1="15" x2="10" y2="15.01"/><line x1="10" y1="17" x2="10" y2="17.01"/>`;
  } else if (variant === "img") {
    inner = `<circle cx="10" cy="13" r="1.5"/><path d="m20 17-3-3-5 5"/>`;
  } else if (variant === "vid") {
    inner = `<polygon points="10 12 16 15 10 18"/>`;
  }
  return `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${base}${inner}</svg>`;
}

function looksLikeAttachmentLink(a: HTMLAnchorElement): boolean {
  if (a.classList.contains("rte-file-chip")) return true;
  if (a.hasAttribute("data-mime") || a.hasAttribute("data-name")) return true;
  if (a.hasAttribute("download")) return true;
  return false;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!
  );
}

function rebuildChip(a: HTMLAnchorElement): void {
  const href = a.getAttribute("href") || "";
  let fileName =
    a.getAttribute("data-name") || a.getAttribute("download") || a.textContent?.trim() || "File";
  // legacy chips may have collapsed name+size into the visible text — strip a trailing size
  fileName = fileName.replace(/\s*\d+(?:\.\d+)?\s*(B|KB|MB|GB)\s*$/i, "").trim() || fileName;
  // also strip if the filename incorrectly ends with a size token glued on
  let mime = a.getAttribute("data-mime") || "";
  if (!mime) {
    try {
      const ext = (
        new URL(href, window.location.origin).pathname.split(".").pop() || ""
      ).toLowerCase();
      mime = EXT_MIME[ext] || EXT_MIME[fileName.split(".").pop()?.toLowerCase() || ""] || "";
    } catch {
      mime = EXT_MIME[fileName.split(".").pop()?.toLowerCase() || ""] || "";
    }
  }
  const sizeAttr = Number(a.getAttribute("data-size")) || 0;
  const sizeText = humanSize(sizeAttr);
  const variant = variantFor(mime, fileName);

  a.classList.add("rte-file-chip");
  if (!a.hasAttribute("target")) a.setAttribute("target", "_blank");
  if (!a.hasAttribute("rel")) a.setAttribute("rel", "noopener noreferrer");
  if (!a.hasAttribute("data-name")) a.setAttribute("data-name", fileName);
  if (mime && !a.hasAttribute("data-mime")) a.setAttribute("data-mime", mime);
  // strip legacy inline color/underline so chip styles can take over
  a.style.color = "";
  a.style.textDecoration = "";

  a.innerHTML =
    `<span class="rte-file-icon rte-file-icon--${variant}">${iconSvg(variant)}</span>` +
    `<span class="rte-file-chip__body">` +
    `<span class="rte-file-chip__name">${escapeHtml(fileName)}</span>` +
    (sizeText ? `<span class="rte-file-chip__meta">${escapeHtml(sizeText)}</span>` : "") +
    `</span>`;
}

const enhance = () => {
  const root = host.value;
  if (!root) return;

  const imgs = Array.from(root.querySelectorAll<HTMLImageElement>("img"));
  const newSrcs = imgs.map((i) => i.src);
  if (newSrcs.join("|") !== images.value.join("|")) {
    images.value = newSrcs;
  }
  imgs.forEach((img, idx) => {
    if (!img.hasAttribute("loading")) img.setAttribute("loading", "lazy");
    if (!img.hasAttribute("decoding")) img.setAttribute("decoding", "async");
    if (img.style.cursor !== "zoom-in") img.style.cursor = "zoom-in";
    const nextIdx = String(idx);
    if (img.dataset.nplbIndex !== nextIdx) img.dataset.nplbIndex = nextIdx;
  });
  Array.from(root.querySelectorAll<HTMLVideoElement>("video")).forEach((v) => {
    v.setAttribute("preload", "metadata");
    v.setAttribute("controls", "controls");
    v.setAttribute("playsinline", "true");
  });
  Array.from(root.querySelectorAll<HTMLAudioElement>("audio")).forEach((a) => {
    a.setAttribute("preload", "metadata");
    a.setAttribute("controls", "controls");
  });

  Array.from(root.querySelectorAll<HTMLAnchorElement>("a[href]")).forEach((a) => {
    if (!looksLikeAttachmentLink(a)) return;
    // skip if already rebuilt by us
    if (a.querySelector(".rte-file-chip__body") && a.querySelector(".rte-file-icon")) return;
    rebuildChip(a);
  });
};

const onClick = (e: Event) => {
  const target = e.target as HTMLElement;

  const chip = target.closest("a.rte-file-chip") as HTMLAnchorElement | null;
  if (chip) {
    e.preventDefault();
    e.stopPropagation();
    const url = chip.getAttribute("href") || "";
    if (!url) return;
    filePreviewAttachment.value = {
      id: url,
      fileName: chip.getAttribute("data-name") || chip.getAttribute("download") || "File",
      mimeType: chip.getAttribute("data-mime") || "",
      fileSize: Number(chip.getAttribute("data-size")) || undefined,
      url,
    };
    filePreviewOpen.value = true;
    return;
  }

  if (target.tagName === "IMG" && target.dataset.nplbIndex != null) {
    e.preventDefault();
    startIndex.value = Number(target.dataset.nplbIndex);
    lightboxOpen.value = true;
  }
};

const close = () => {
  lightboxOpen.value = false;
};

watch(
  () => props.html,
  async () => {
    await nextTick();
    enhance();
  }
);

onMounted(() => {
  enhance();
  clickHandler = onClick;
  host.value?.addEventListener("click", clickHandler);
});

onBeforeUnmount(() => {
  if (clickHandler && host.value) {
    host.value.removeEventListener("click", clickHandler);
  }
});
</script>

<style scoped>
.note-preview :deep(img) {
  max-width: 100%;
  max-height: 320px;
  height: auto;
  border-radius: 6px;
  display: block;
  margin: 6px 0;
  object-fit: contain;
}
.note-preview :deep(video) {
  display: block;
  width: 100%;
  max-width: 480px;
  max-height: 360px;
  border-radius: 10px;
  margin: 8px 0;
  background: #0f172a;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  outline: none;
}
.note-preview :deep(audio) {
  display: block;
  width: 100%;
  max-width: 380px;
  min-height: 40px;
  margin: 8px 0;
  border-radius: 999px;
  outline: none;
}
.note-preview :deep(audio::-webkit-media-controls-panel) {
  background-color: #eef2ff;
}
.note-preview :deep(iframe) {
  max-width: 100%;
  aspect-ratio: 16 / 9;
  width: 100%;
  border-radius: 6px;
  margin: 6px 0;
  border: 0;
}
.note-preview :deep(a) {
  color: #2d60ff;
  text-decoration: underline;
}

.note-preview :deep(a.rte-file-chip) {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  margin: 4px 4px 4px 0;
  border-radius: 8px;
  background: #eef2ff;
  color: #1e1b4b !important;
  text-decoration: none !important;
  border: 1px solid #e0e7ff;
  vertical-align: middle;
  line-height: 1.2;
  font-size: 12px;
  max-width: 100%;
  cursor: pointer;
  transition: background 0.15s ease;
}
.note-preview :deep(a.rte-file-chip:hover) {
  background: #e0e7ff;
}
.note-preview :deep(a.rte-file-chip .rte-file-chip__body) {
  display: inline-flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}
.note-preview :deep(a.rte-file-chip .rte-file-chip__name) {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 280px;
  color: #1e1b4b;
}
.note-preview :deep(a.rte-file-chip .rte-file-chip__meta) {
  font-size: 10px;
  color: #6b7280;
}
.note-preview :deep(a.rte-file-chip .rte-file-icon) {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #ffffff;
}
.note-preview :deep(a.rte-file-chip .rte-file-icon svg) {
  width: 18px;
  height: 18px;
  display: block;
}
.note-preview :deep(.rte-file-icon--pdf) {
  color: #dc2626;
}
.note-preview :deep(.rte-file-icon--img) {
  color: #0ea5e9;
}
.note-preview :deep(.rte-file-icon--vid) {
  color: #7c3aed;
}
.note-preview :deep(.rte-file-icon--aud) {
  color: #db2777;
}
.note-preview :deep(.rte-file-icon--doc) {
  color: #2563eb;
}
.note-preview :deep(.rte-file-icon--xls) {
  color: #059669;
}
.note-preview :deep(.rte-file-icon--zip) {
  color: #ea580c;
}
.note-preview :deep(.rte-file-icon--file) {
  color: #6b7280;
}
</style>
